import { useMemo } from 'react';
import type { PermissionEntry } from '@/lib/api';

interface PermissionPickerProps {
  permissions: PermissionEntry[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

// Groups the flat permission catalog by app_id and renders a checkbox tree
// with "select all for this app" toggles. Selection state is expressed as an
// array of permission action strings (e.g. "epo.epo.read").
export function PermissionPicker({ permissions, selected, onChange, disabled }: PermissionPickerProps) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const grouped = useMemo(() => {
    const map = new Map<string, PermissionEntry[]>();
    for (const perm of permissions) {
      const appId = perm.app_id || 'hub';
      const list = map.get(appId) ?? [];
      list.push(perm);
      map.set(appId, list);
    }
    return Array.from(map.entries())
      .map(([appId, perms]) => ({
        appId,
        permissions: perms.sort((a, b) => a.action.localeCompare(b.action)),
      }))
      .sort((a, b) => a.appId.localeCompare(b.appId));
  }, [permissions]);

  function togglePermission(action: string, checked: boolean) {
    if (checked) {
      if (selectedSet.has(action)) return;
      onChange([...selected, action]);
    } else {
      onChange(selected.filter(a => a !== action));
    }
  }

  function toggleApp(perms: PermissionEntry[], checked: boolean) {
    const appActions = perms.map(p => p.action);
    if (checked) {
      const next = new Set(selected);
      for (const action of appActions) next.add(action);
      onChange(Array.from(next));
    } else {
      const drop = new Set(appActions);
      onChange(selected.filter(a => !drop.has(a)));
    }
  }

  return (
    <div className="space-y-3 rounded-md border p-3 max-h-80 overflow-y-auto">
      {grouped.length === 0 ? (
        <p className="text-sm text-muted-foreground">No permissions in the catalog.</p>
      ) : (
        grouped.map(({ appId, permissions: perms }) => {
          const appChecked = perms.every(p => selectedSet.has(p.action));
          const appPartial = !appChecked && perms.some(p => selectedSet.has(p.action));
          const selectedCount = perms.filter(p => selectedSet.has(p.action)).length;

          return (
            <div key={appId} className="space-y-1">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={appChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = appPartial;
                  }}
                  disabled={disabled}
                  onChange={(e) => toggleApp(perms, e.target.checked)}
                />
                <span className="capitalize">{appId}</span>
                <span className="text-xs text-muted-foreground">
                  ({selectedCount}/{perms.length})
                </span>
              </label>
              <div className="pl-6 space-y-1">
                {perms.map(perm => (
                  <label
                    key={perm.id}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selectedSet.has(perm.action)}
                      disabled={disabled}
                      onChange={(e) => togglePermission(perm.action, e.target.checked)}
                    />
                    <span>
                      <span className="font-mono text-xs">{perm.action}</span>
                      {perm.description && (
                        <span className="block text-xs text-muted-foreground">
                          {perm.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
